import { useCallback, useEffect, useState } from "react";
import { getRelatedProjects } from "../lib/api";
import type { FoundProject, RelatedProjects } from "../lib/types";
import { formatApiError } from "../lib/utils";
import { Icon } from "./Icon";
import { Button } from "./ui";

function ProjectLink({
  project,
  detail,
  onOpenProject,
}: {
  project: FoundProject;
  detail: string;
  onOpenProject?: (projectId: string) => void;
}) {
  return (
    <li className="flex items-start justify-between gap-3 border-t border-outline-variant/50 pt-2 first:border-0 first:pt-0">
      <div className="min-w-0">
        {onOpenProject ? (
          <button
            type="button"
            className="text-body-sm text-primary hover:underline text-left"
            onClick={() => onOpenProject(project.project_id)}
          >
            {project.project_name || project.project_id}
          </button>
        ) : (
          <span className="text-body-sm text-on-surface">{project.project_name || project.project_id}</span>
        )}
        <p className="text-[10px] font-data-mono text-on-surface-variant mt-0.5">{detail}</p>
      </div>
      {project.status && project.status !== "active" ? (
        <span className="text-[9px] uppercase font-data-mono text-outline shrink-0">{project.status}</span>
      ) : null}
    </li>
  );
}

/**
 * Projects connected to this one, each list saying how: found by this
 * project's lead research, linked by an operator, or sharing source documents.
 */
export function RelatedProjectsPanel({
  projectId,
  refreshKey = 0,
  onOpenProject,
}: {
  projectId: string;
  refreshKey?: number;
  onOpenProject?: (projectId: string) => void;
}) {
  const [data, setData] = useState<RelatedProjects | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      setData(await getRelatedProjects(projectId));
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const found = data?.found_by_leads || [];
  const linked = data?.linked || [];
  const shared = data?.shared_sources || [];
  const empty = !loading && !error && !found.length && !linked.length && !shared.length;

  return (
    <section className="panel-border bg-surface-container-low p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[10px] uppercase tracking-widest text-on-surface-variant font-data-label flex items-center gap-2">
            <Icon name="account_tree" className="text-base" />
            Related projects
          </h3>
          <p className="text-body-sm text-on-surface-variant mt-1">
            Other projects this one connects to, and how they are connected.
          </p>
        </div>
        <Button variant="link-muted" size="sm" onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      </div>
      {error ? <p className="text-body-sm text-error">{error}</p> : null}
      {loading && !data ? <p className="text-body-sm text-on-surface-variant">Loading related projects…</p> : null}
      {empty ? (
        <p className="text-body-sm text-on-surface-variant">
          None yet. Projects found by this project&apos;s lead research, projects an operator linked, and projects
          that share a source document appear here.
        </p>
      ) : null}

      {found.length ? (
        <div>
          <h4 className="text-[10px] uppercase tracking-widest text-tertiary font-data-label mb-2">
            Found by this project&apos;s leads ({found.length})
          </h4>
          <ul className="space-y-2">
            {found.map((project) => (
              <ProjectLink
                key={project.project_id}
                project={project}
                onOpenProject={onOpenProject}
                detail={`${project.target_company} · ${project.documents} document${project.documents === 1 ? "" : "s"}${project.leads?.length ? ` · via ${project.leads.join("; ")}` : ""}`}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {linked.length ? (
        <div>
          <h4 className="text-[10px] uppercase tracking-widest text-primary font-data-label mb-2">
            Linked ({linked.length})
          </h4>
          <ul className="space-y-2">
            {linked.map((project) => (
              <ProjectLink
                key={`${project.project_id}-${project.relationship}-${project.basis}-${project.direction}`}
                project={project}
                onOpenProject={onOpenProject}
                detail={`${(project.relationship || "related").replace(/_/g, " ")} · ${project.basis || ""}${project.direction === "incoming" ? " · points here" : ""}`}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {shared.length ? (
        <div>
          <h4 className="text-[10px] uppercase tracking-widest text-secondary font-data-label mb-2">
            Share source documents ({shared.length})
          </h4>
          <ul className="space-y-2">
            {shared.map((project) => (
              <ProjectLink
                key={project.project_id}
                project={project}
                onOpenProject={onOpenProject}
                detail={`${project.target_company} · ${project.documents} shared document${project.documents === 1 ? "" : "s"}`}
              />
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
